import axios from 'axios';

// Get API base URL
const getApiBase = () => {
  return process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
};

// Get authentication headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Generic API call wrapper
const apiCall = async (endpoint, options = {}) => {
  const url = `${getApiBase()}${endpoint}`;
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers
    }
  };

  try {
    const response = await axios(url, config);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.error || error.response.data?.message || 'API request failed');
    } else if (error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('Request setup failed');
    }
  }
};

export const questionnaireLeadsAPI = {
  /**
   * Fetch questionnaire leads with optional filtering and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of results per page (default: 50)
   * @param {number} params.offset - Number of results to skip (default: 0)
   * @param {string} params.stepName - Filter by step name
   * @param {number} params.days - Filter by number of days back (default: 7)
   * @returns {Promise<Object>} API response with leads data and pagination info
   */
  async getLeads(params = {}) {
    const queryParams = new URLSearchParams();
    
    // Set default values
    const {
      limit = 50,
      offset = 0,
      stepName,
      days = 7
    } = params;

    queryParams.append('limit', limit.toString());
    queryParams.append('offset', offset.toString());
    queryParams.append('days', days.toString());

    if (stepName && stepName.trim()) {
      queryParams.append('stepName', stepName.trim());
    }

    try {
      const response = await apiCall(`/api/questionnaire/leads?${queryParams.toString()}`, {
        method: 'GET'
      });

      return response;
    } catch (error) {
      console.error('Error fetching questionnaire leads:', error);
      throw new Error(error.message || 'Failed to fetch questionnaire leads');
    }
  },

  /**
   * Get leads summary/statistics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Summary statistics
   */
  async getLeadsSummary(params = {}) {
    // For now, we'll calculate from the main endpoint
    // This could be optimized with a dedicated summary endpoint later
    try {
      const response = await this.getLeads({ ...params, limit: 1 });
      return {
        success: true,
        data: {
          totalLeads: response.data?.pagination?.total || 0
        }
      };
    } catch (error) {
      console.error('Error fetching leads summary:', error);
      throw error;
    }
  }
};

export default questionnaireLeadsAPI;